import { useState } from "react";
import { Loader2, Search, CheckCircle2, Circle, XCircle } from "lucide-react";
import { Input } from "../ui/input";
import { Field, inputCls } from "./Field";
import { api, errMsg, formatDate } from "../../lib/api";
import { MemberCardButton } from "./MemberCard";

const STEPS = [
  { key: "submitted", label: "Submitted" },
  { key: "under_review", label: "Under Review" },
  { key: "approved", label: "Approved" },
];

const Timeline = ({ status }) => {
  if (status === "rejected") {
    return <p className="mt-6 flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm font-semibold text-red-700"><XCircle className="h-5 w-5" /> Application not approved. Please contact the federation office for details.</p>;
  }
  const idx = STEPS.findIndex((s) => s.key === status);
  return (
    <ol className="mt-6 grid grid-cols-3 gap-2" data-testid="track-timeline">
      {STEPS.map((s, i) => (
        <li key={s.key} className={`flex flex-col items-center gap-2 rounded-lg p-3 text-center text-xs font-semibold ${i <= idx ? "bg-leaf-light text-leaf-dark" : "bg-slate-100 text-slate-400"}`}>
          {i <= idx ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}{s.label}
        </li>
      ))}
    </ol>
  );
};

export const TrackStatus = () => {
  const [ref, setRef] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setRes(null);
    try {
      const { data } = await api.post("/applications/track", { ref_id: ref, mobile });
      setRes(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10" data-testid="track-section">
      <h3 className="font-display text-xl font-bold text-navy">Track your membership application</h3>
      <p className="mt-2 text-sm text-slate-600">Enter the reference number you received on submission and your registered mobile number.</p>
      <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <Field label="Reference Number" required><Input className={`${inputCls} font-mono uppercase`} required placeholder="ITEF-XXXX-XXXXXX" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="track-ref-input" /></Field>
        <Field label="Mobile Number" required><Input className={inputCls} required inputMode="numeric" value={mobile} onChange={(e) => setMobile(e.target.value)} data-testid="track-mobile-input" /></Field>
        <button type="submit" disabled={busy} className="btn-primary h-11 disabled:opacity-60" data-testid="track-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Track
        </button>
      </form>
      {error && <p className="mt-5 rounded-lg bg-red-50 p-4 text-sm text-red-700" data-testid="track-error">{error}</p>}
      {res && (
        <div className="mt-6 rounded-xl border border-slate-200 p-5" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><p className="text-xs text-slate-500">Applicant</p><p className="font-semibold text-navy">{res.full_name}</p></div>
            <div className="text-right"><p className="text-xs text-slate-500">Submitted on</p><p className="font-semibold text-navy">{formatDate(res.created_at)}</p></div>
          </div>
          <Timeline status={res.status} />
          {res.member_no && <p className="mt-4 text-sm text-slate-600">Membership number: <span className="font-mono font-bold text-royal" data-testid="track-member-no">{res.member_no}</span></p>}
          {res.status === "approved" && res.member_no && <MemberCardButton refId={res.ref_id} mobile={mobile} />}
        </div>
      )}
    </div>
  );
};
